import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { apiService } from '../../services/api'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcEye = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
)
const IcUsers = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
)
const IcFile = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
)
const IcArrowUp = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
  </svg>
)
const IcUpload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
)
const IcAlert = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
)
const IcDesktop = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
)
const IcMobile = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
)
const IcTablet = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
  </svg>
)

const DEVICE_ICONS = { Desktop: IcDesktop, Mobile: IcMobile, Tablet: IcTablet }
const RANGES = ['7d', '30d', '90d']
const SRC_COLORS = ['#6c63ff', '#5BC0EB', '#E8654F', '#1FB596', '#f59e0b']

// ── Area chart ──────────────────────────────────────────────────────────────
function AreaChart({ data, height = 240 }) {
  const W = 800, H = height, PAD = { t: 10, r: 0, b: 4, l: 0 }
  if (!data || data.length < 2) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Not enough data for this period yet</div>
  }
  const vals = data.map(d => d.views || d.count || 0)
  const max = Math.max(...vals, 1)
  const plotH = H - PAD.t - PAD.b
  const pts = vals.map((v, i) => ({ x: (i / (vals.length - 1)) * W, y: PAD.t + plotH - (v / max) * plotH }))
  const line = pts.map(p => `${p.x},${p.y}`).join(' ')
  const area = [...pts.map(p => `${p.x},${p.y}`), `${W},${H}`, `0,${H}`].join(' ')
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        <linearGradient id="clVisits" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7C5CFF" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#7C5CFF" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#clVisits)" />
      <polyline points={line} fill="none" stroke="#7C5CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ── Bar chart (hour by hour) ──────────────────────────────────────────────────
function BarChart({ data, height = 130 }) {
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height }}>
      {data.map((d, i) => (
        <div key={i} title={`${d.name} — ${d.value} visit${d.value === 1 ? '' : 's'}`}
          style={{ flex: 1, height: `${Math.max((d.value / max) * 100, 2)}%`, background: 'rgba(124, 92, 255, 0.45)', borderRadius: '3px 3px 0 0', minHeight: 2, transition: 'height 0.2s ease' }} />
      ))}
    </div>
  )
}

// ── Donut (devices) ───────────────────────────────────────────────────────────
function polarToCart(cx, cy, r, deg) { const rad = (deg * Math.PI) / 180; return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) } }
function compactNum(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(n >= 10000000 ? 0 : 1).replace(/\.0$/, '') + 'm'
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'k'
  return String(n)
}
function Donut({ data, size = 120, thickness = 16, centerLabel, centerValue }) {
  const R = size / 2, r = R - thickness, cx = R, cy = R
  const total = data.reduce((s, d) => s + d.value, 0) || 1
  let angle = -90
  const arcs = data.filter(d => d.value > 0).map(d => {
    const deg = (d.value / total) * 360
    const start = polarToCart(cx, cy, R, angle)
    const startInner = polarToCart(cx, cy, r, angle)
    angle += deg
    const end = polarToCart(cx, cy, R, angle - 0.5)
    const endInner = polarToCart(cx, cy, r, angle - 0.5)
    const large = deg > 180 ? 1 : 0
    return { d: `M ${start.x} ${start.y} A ${R} ${R} 0 ${large} 1 ${end.x} ${end.y} L ${endInner.x} ${endInner.y} A ${r} ${r} 0 ${large} 0 ${startInner.x} ${startInner.y} Z`, color: d.color }
  })
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} />)}
      {centerValue != null && (
        <text x={cx} y={cy - 2} textAnchor="middle" dominantBaseline="middle"
          style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 600, fill: 'var(--text)' }}>{centerValue}</text>
      )}
      {centerLabel && (
        <text x={cx} y={cy + 16} textAnchor="middle" dominantBaseline="middle"
          style={{ fontSize: 10, fill: 'var(--text-muted)' }}>{centerLabel}</text>
      )}
    </svg>
  )
}

// ── KPI card (prototype ClKpi) ─────────────────────────────────────────────────
function ClKpi({ label, value, delta, up, icon }) {
  return (
    <div className="card card-pad-sm">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span className="icon-tile" style={{ width: 30, height: 30, fontSize: 14 }}>{icon}</span>
        {delta != null && (
          <span className={up ? 'delta-up' : 'delta-down'} style={{ fontSize: 11.5 }}>
            {up && <IcArrowUp />}{typeof delta === 'number' ? `${delta > 0 ? '+' : ''}${delta}%` : delta}
          </span>
        )}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{label}</div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const ClientAnalytics = () => {
  const [range, setRange] = useState('30d')

  const { data: profile } = useQuery('client-profile', () => apiService.get('/clients/profile').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const { data: raw, isLoading, isFetching, isError, refetch } = useQuery(
    ['client-analytics', range],
    () => apiService.get(`/analytics/overview?range=${range}`).then(r => r.data),
    { staleTime: 120000, keepPreviousData: true, retry: 2 }
  )

  const data = raw || {}
  const {
    totalPageViews = 0,
    uniqueVisitors = 0,
    activityData = [],
    topPages = [],
    deviceTypes = [],
    referrerData = [],
    change = {},
    hourlyToday = [],
    topLocations = [],
  } = data

  const domain = data.sites?.[0]?.domain || profile?.company_name || 'your site'
  const visitorsUp = (change.uniqueVisitors ?? 0) >= 0
  const topPage = topPages[0]
  const mobile = deviceTypes.find(d => d.name === 'Mobile')
  const maxPage = topPages[0]?.views || 1
  const rangeLabel = { '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days' }[range]

  if (isError) {
    return (
      <div className="card card-pad" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <IcAlert />
        <div style={{ marginTop: 10, fontSize: 14, color: 'var(--text-muted)' }}>Failed to load analytics data.</div>
        <button className="btn btn-sm" style={{ marginTop: 14 }} onClick={refetch}>Retry</button>
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>Analytics</h1>
          <div className="subtitle">
            How <strong style={{ color: 'var(--text)' }}>{domain}</strong> is performing · {isFetching && !isLoading ? 'Updating…' : 'Updated just now'}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <div className="tabs">
            {RANGES.map(r => (
              <button key={r} className={range === r ? 'active' : ''} onClick={() => setRange(r)}>{r}</button>
            ))}
          </div>
          <button className="btn"><IcUpload /> Export</button>
        </div>
      </div>

      <div className="page-body" style={{ gap: 18 }}>
        {/* KPI cards */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <ClKpi label="Visitors" value={uniqueVisitors.toLocaleString()} delta={change.uniqueVisitors} up={visitorsUp} icon={<IcUsers />} />
          <ClKpi label="Page views" value={totalPageViews.toLocaleString()} delta={change.pageViews} up={(change.pageViews ?? 0) >= 0} icon={<IcEye />} />
          <ClKpi label="Top page" value={topPage ? topPage.page : '—'} icon={<IcFile />} />
          <ClKpi label="Mobile share" value={mobile ? `${mobile.value}%` : '—'} icon={<IcMobile />} />
        </div>

        {/* Big chart */}
        <div className="card card-pad">
          <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
            <div>
              <h2 className="h2">Visitors over time</h2>
              <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>{rangeLabel}</div>
            </div>
            {change.uniqueVisitors != null && (
              <span className={`badge badge-${visitorsUp ? 'success' : 'danger'}`}>
                {visitorsUp && <IcArrowUp />} {change.uniqueVisitors > 0 ? '+' : ''}{change.uniqueVisitors}% vs last period
              </span>
            )}
          </div>
          <div className="stat-row" style={{ marginBottom: 12 }}>
            <div className="num">{uniqueVisitors.toLocaleString()}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>visitors this period</div>
          </div>
          {isLoading ? (
            <div style={{ height: 240, background: 'var(--border)', borderRadius: 8, opacity: .25 }} />
          ) : (
            <AreaChart data={activityData} height={240} />
          )}
        </div>

        {/* Top pages + sources + devices */}
        <div className="bento" style={{ gridTemplateColumns: '1.3fr 1fr 1fr', gap: 14, alignItems: 'stretch' }}>
          {/* Most visited pages */}
          <div className="card card-pad">
            <h2 className="h2">Most visited pages</h2>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 14 }}>Where people spend their time</div>
            {topPages.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12.5 }}>No page visits recorded yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {topPages.slice(0, 6).map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 120, minWidth: 120, overflow: 'hidden' }}>
                      <div style={{ fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.page === '/' ? 'Home' : p.page.replace(/^\//, '')}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.page}</div>
                    </div>
                    <div className="progress thin" style={{ flex: 1 }}><div style={{ width: `${Math.round((p.views / maxPage) * 100)}%` }} /></div>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-soft)', width: 48, textAlign: 'right' }}>{p.views.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* How people find you */}
          <div className="card card-pad">
            <h2 className="h2">How people find you</h2>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 14 }}>Traffic sources</div>
            {referrerData.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12.5 }}>No referrer data yet</div>
            ) : (
              referrerData.map((s, i) => {
                const color = s.color || SRC_COLORS[i % SRC_COLORS.length]
                return (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: color, marginRight: 8 }} />
                      <span style={{ fontSize: 12.5 }}>{s.source}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-soft)' }}>{s.percentage}%</span>
                    </div>
                    <div className="progress thin"><div style={{ width: `${Math.min(s.percentage, 100)}%`, background: color }} /></div>
                  </div>
                )
              })
            )}
          </div>

          {/* Devices */}
          <div className="card card-pad">
            <h2 className="h2">Devices</h2>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 16 }}>What visitors use</div>
            {deviceTypes.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12.5 }}>No device data yet</div>
            ) : (
              <div className="flex items-center gap-4">
                <Donut size={120} thickness={16} data={deviceTypes}
                  centerLabel="visits" centerValue={compactNum(deviceTypes.reduce((s, d) => s + (d.count || 0), 0))} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {deviceTypes.map((d, i) => {
                    const Icon = DEVICE_ICONS[d.name] || IcDesktop
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                        <span style={{ color: d.color, display: 'inline-flex' }}><Icon /></span>
                        <span style={{ color: 'var(--text-soft)' }}>{d.name}</span>
                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>{d.value}%</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hourly today + locations */}
        <div className="bento" style={{ gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Visitors today</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Hour by hour</div>
              </div>
            </div>
            {hourlyToday.length === 0 ? (
              <div style={{ height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 12.5 }}>No visits yet today</div>
            ) : (
              <>
                <BarChart data={hourlyToday} height={130} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-faint)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
                  <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>now</span>
                </div>
              </>
            )}
          </div>

          <div className="card card-pad">
            <h2 className="h2">Where your visitors are</h2>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 14 }}>Top locations this period</div>
            {topLocations.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12.5 }}>No location data yet — it starts collecting as new visitors arrive.</div>
            ) : (
              topLocations.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 15 }}>{r.flag}</span>
                  <span style={{ fontSize: 12.5, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label || 'Unknown'}</span>
                  <div className="progress thin" style={{ width: 50 }}><div style={{ width: `${r.percentage}%` }} /></div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-soft)', width: 44, textAlign: 'right' }}>{r.views.toLocaleString()}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ClientAnalytics
